import { lazy } from "react";
import { Navigate, useLocation, Route } from "react-router-dom";
import FirstAndLastMile from "src/_modules/layouts/FirstAndLastMile";
import LazyRouteWrapper from "../LazyRouteWrapper";
import { CodInvoices } from "src/_modules/MapitShip";

const Dashboard = lazy(() => import("src/views/Dashboard"));
const LanguageSetting = lazy(() => import("src/modules/Settings/LanguageSetting"));
const WhatsappMessagesSettings = lazy(() => import("src/modules/Settings/WhatsappMessagesSettings"));
const ZoneOrderList = lazy(() => import('src/modules/Order/ZoneOrderList'));
const OrderList = lazy(() => import("src/modules/Order/OrderList"));
const AddOrder = lazy(() => import('src/modules/Order/OrderManagement'));
const OrderDetails = lazy(() => import("src/modules/Order/OrderDetails"));
const Orders = lazy(() => import("src/modules/Order/Orders"));
const DriverList = lazy(() => import('src/_modules/Users/Driver/DriverList'));
const AddDriver = lazy(() => import('src/_modules/Users/Driver/DriverManagement'));
const DriverDetails = lazy(() => import("src/_modules/Users/Driver/DriverDetails"));
const ZoneList = lazy(() => import("src/views/ZoneList"));
const Integrations = lazy(() => import('src/views/Integrations/Integrations'));
// const ZIDIntegrationCheckStatus = lazy(() => import('src/views/Integrations/ZIDIntegrationCheckStatus'));
// const ZIDIntegrationRedirect = lazy(() => import('src/views/Integrations/ZIDIntegrationRedirect'));
const TrackDrivers = lazy(() => import("src/_modules/Users/Driver/TrackDrivers"));
const RecentOrders = lazy(() => import("src/modules/Order/OrdersFilter/RecentOrders"));
const Connection = lazy(() => import("src/modules/Connection"));
const Warehouse = lazy(() => import("src/modules/Warehouse"));
const SubscriptionManagement = lazy(() => import("src/views/SubscriptionManagement"));
const WarehouseGuard = lazy(() => import("src/_modules/Guard/GuardManagements"));
const ZoneOrdersCount = lazy(() => import("src/_modules/Tasks/ShippingCompany/ZoneOrdersCount"));
const ZoneOrders = lazy(() => import("src/_modules/Tasks/ShippingCompany/ZoneOrders"));
const ReturnedTasks = lazy(() => import("src/_modules/Tasks/ShippingCompany/ReturnedTasks"));
const EditTask = lazy(() => import("src/_modules/Tasks/ShippingCompany/EditTask"));
const TaskDetails = lazy(() => import("src/_modules/Tasks/ShippingCompany/TaskDetails"));
const OrdersList = lazy(() => import("src/_modules/Tasks/ShippingCompany/OrdersList"));
const CreateTask = lazy(() => import("src/_modules/Tasks/ShippingCompany/CreateTask"));
const ZonesTasksCount = lazy(() => import("src/_modules/Tasks/ShippingCompany/ZonesTasksCount"));
const WarehousesOrdersCount = lazy(() => import("src/_modules/Tasks/ShippingCompany/WarehousesOrdersCount"));
const WarehouseOrdersList = lazy(() => import("src/_modules/Tasks/ShippingCompany/WarehouseOrdersList"));
const ZoneTasksList = lazy(() => import("src/_modules/Tasks/ShippingCompany/ZoneTasksList"));
const TaskOrderDetails = lazy(() => import("src/_modules/Tasks/ShippingCompany/OrderDetails"));
const TasksList = lazy(() => import("src/_modules/Tasks/ShippingCompany/TasksList"));
const WarehouseOrdersZone = lazy(() => import("src/_modules/Tasks/ShippingCompany/WarehouseOrdersZone"));
const TasksCount = lazy(() => import("src/_modules/Tasks/ShippingCompany/TasksCount"));
const MerchantCreateTask = lazy(() => import("src/_modules/Tasks/Merchant/MerchantCreateTask"));
// const RecentTasks = lazy(() => import("src/_modules/Tasks/TasksFilter/RecentTasks"));
const AccountPlans = lazy(() => import("src/_modules/Account/AccountPlans"));
const AccountSubscription = lazy(() => import("src/_modules/Account/AccountSubscription"));
const MerchantAccountBasicInfo = lazy(() => import("src/_modules/Account/MerchantAccountBasicInfo"));
const MerchantAccountSettings = lazy(() => import("src/_modules/Account/MerchantAccountSettings"));
const PlanCheckout = lazy(() => import("src/_modules/Account/PlanCheckout"));
const Users = lazy(() => import("src/_modules/Users/Users/Users"));
const Profile = lazy(() => import("src/_modules/Users/Profile"));
const Customizations = lazy(() => import("src/_modules/Customizations/Customizations"));
const Transactions = lazy(() => import("src/_modules/Transactions"));
const PickupPoints = lazy(() => import("src/_modules/PickupPoints"));
const NotFound = lazy(() => import("src/_modules/common/NotFound"));
const ShippingPartnersList = lazy(() => import("src/modules/Admin/ShippingPartners/ShippingPartnersList"));
const AccountBalance = lazy(() => import("src/_modules/MapitShip/views/AccountBalance"));
const NationalAddressTransactions = lazy(() => import("src/_modules/NationalAddressTransactions"));
const AdminOrdersFilter = lazy(() => import("src/modules/Order/OrdersFilter/AdminOrdersFilter"));
const AdminTasksFilter = lazy(() => import("src/_modules/Tasks/TasksFilter/AdminTasksFilter"));

const CheckBeforeEnterMerchant = (currentUser) => {
    const location = useLocation()
    if (location.pathname !== "/" && currentUser?.merchant?.currentStatus === "BLOCKED") {
        return <Navigate to="/blocked" />
    } else if (currentUser) {
        return <FirstAndLastMile />
    }
    // if (location.pathname === "/merchant/integrations/zid") {
    //     return <Navigate to="/auth/general?redirect_url=/merchant/integrations/zid" />
    // }
    return <Navigate to="/auth/general" />
}

const merchantRoutes = (currentUser) => (
    <>
        <Route path="merchant" element={CheckBeforeEnterMerchant(currentUser)}>
            <Route path="" element={<LazyRouteWrapper><Dashboard /></LazyRouteWrapper>} />
            <Route path="tasks">
                <Route path="create" element={<LazyRouteWrapper><CreateTask /></LazyRouteWrapper>} />
                <Route path="returned" element={<LazyRouteWrapper><ReturnedTasks /></LazyRouteWrapper>} />
                <Route path="basic/create" element={<LazyRouteWrapper><MerchantCreateTask /></LazyRouteWrapper>} />
                <Route path="filter" element={<LazyRouteWrapper><AdminTasksFilter /></LazyRouteWrapper>} />
                <Route path="zones" element={<LazyRouteWrapper><ZonesTasksCount /></LazyRouteWrapper>} />
                <Route path="zone_tasks/:zoneId" element={<LazyRouteWrapper><ZoneTasksList /></LazyRouteWrapper>} />
                <Route path="completed" element={<LazyRouteWrapper><TasksList /></LazyRouteWrapper>} />
                <Route path=":taskId" element={<LazyRouteWrapper><TaskDetails /></LazyRouteWrapper>} />
                <Route path="archived/:taskId" element={<LazyRouteWrapper><TaskDetails /></LazyRouteWrapper>} />
                <Route path="edit/:taskId" element={<LazyRouteWrapper><EditTask /></LazyRouteWrapper>} />
                <Route path="warehouses" element={<LazyRouteWrapper><WarehousesOrdersCount /></LazyRouteWrapper>} />
                <Route path="tasks_count" element={<LazyRouteWrapper><TasksCount /></LazyRouteWrapper>} />
                <Route path="warehouse_orders/:warehouseId" element={<LazyRouteWrapper><WarehouseOrdersList /></LazyRouteWrapper>} />
                <Route path="orders" element={<LazyRouteWrapper><OrdersList /></LazyRouteWrapper>} />
                <Route path="zone_orders_count" element={<LazyRouteWrapper><ZoneOrdersCount /></LazyRouteWrapper>} />
                <Route path="zone_orders" element={<LazyRouteWrapper><ZoneOrders /></LazyRouteWrapper>} />
                <Route path="order/:id" element={<LazyRouteWrapper><TaskOrderDetails /></LazyRouteWrapper>} />
                <Route path="archived/order/:id" element={<LazyRouteWrapper><TaskOrderDetails /></LazyRouteWrapper>} />
                <Route path="*" element={<Navigate to="/errors/404" />} />
            </Route>
            <Route path="orders">
                <Route path="completed-orders" element={<LazyRouteWrapper><Orders /></LazyRouteWrapper>} />
                <Route path="" element={<LazyRouteWrapper><ZoneOrderList /></LazyRouteWrapper>} />
                <Route path="warehouse_orders_zone" element={<LazyRouteWrapper><WarehouseOrdersZone /></LazyRouteWrapper>} />
                <Route path="list" element={<LazyRouteWrapper><OrderList /></LazyRouteWrapper>} />
                <Route path="add" element={<LazyRouteWrapper><AddOrder /></LazyRouteWrapper>} />
                <Route path="filter" element={<LazyRouteWrapper><AdminOrdersFilter key="orders-filter" /></LazyRouteWrapper>} />
                <Route path=":id" element={<LazyRouteWrapper><OrderDetails /></LazyRouteWrapper>} />
                <Route path="*" element={<Navigate to="/errors/404" />} />
            </Route>
            <Route path="users" element={<LazyRouteWrapper><Users /></LazyRouteWrapper>} />
            <Route path="drivers">
                <Route path="add" element={<LazyRouteWrapper><AddDriver /></LazyRouteWrapper>} />
                <Route path="" element={<LazyRouteWrapper><DriverList /></LazyRouteWrapper>} />
                <Route path=":id" element={<LazyRouteWrapper><DriverDetails /></LazyRouteWrapper>} />
                <Route path="track" element={<LazyRouteWrapper><TrackDrivers /></LazyRouteWrapper>} />
                <Route path="*" element={<Navigate to="/errors/404" />} />
            </Route>
            <Route path="zones">
                <Route path="" element={<LazyRouteWrapper><ZoneList /></LazyRouteWrapper>} />
                <Route path="*" element={<Navigate to="/errors/404" />} />
            </Route>
            <Route path="qrcodes" element={<LazyRouteWrapper><Dashboard /></LazyRouteWrapper>} />
            <Route path="integrations" element={<LazyRouteWrapper><Integrations /></LazyRouteWrapper>} />
            {/* <Route path="integrations/zid" element={<LazyRouteWrapper><ZIDIntegrationRedirect /></LazyRouteWrapper>} />
            <Route path="integrations/zid-status" element={<LazyRouteWrapper><ZIDIntegrationCheckStatus /></LazyRouteWrapper>} /> */}
            <Route path="settings">
                <Route path="language" element={<LazyRouteWrapper><LanguageSetting /></LazyRouteWrapper>} />
                <Route path="whatsapp" element={<LazyRouteWrapper><WhatsappMessagesSettings /></LazyRouteWrapper>} />
                <Route path="*" element={<Navigate to="/errors/404" />} />
            </Route>
            <Route path="profile" element={<LazyRouteWrapper><Profile /></LazyRouteWrapper>} />
            <Route path="account">
                <Route path="" element={<Navigate to="info" />} />
                <Route path="info" element={<LazyRouteWrapper><MerchantAccountBasicInfo /></LazyRouteWrapper>} />
                <Route path="settings" element={<LazyRouteWrapper><MerchantAccountSettings /></LazyRouteWrapper>} />
                <Route path="plans" element={<LazyRouteWrapper><AccountPlans /></LazyRouteWrapper>} />
                <Route path="plan-checkout/:planId/:period" element={<LazyRouteWrapper><PlanCheckout /></LazyRouteWrapper>} />
                <Route path="subscription" element={<LazyRouteWrapper><AccountSubscription /></LazyRouteWrapper>} />
            </Route>
            <Route path="connections" element={<LazyRouteWrapper><Connection /></LazyRouteWrapper>} />
            <Route path="shipping-partners" element={<LazyRouteWrapper><ShippingPartnersList /></LazyRouteWrapper>} />
            <Route path="warehouse" element={<LazyRouteWrapper><Warehouse /></LazyRouteWrapper>} />
            <Route path="warehouse_guard" element={<LazyRouteWrapper><WarehouseGuard /></LazyRouteWrapper>} />
            <Route path="subscriptions" element={<LazyRouteWrapper><SubscriptionManagement /></LazyRouteWrapper>} />
            <Route path="customizations" element={<LazyRouteWrapper><Customizations /></LazyRouteWrapper>} />
            <Route path="transactions" element={<LazyRouteWrapper><Transactions /></LazyRouteWrapper>} />
            <Route path="pickup-points" element={<LazyRouteWrapper><PickupPoints /></LazyRouteWrapper>} />
            <Route path="not-found" element={<LazyRouteWrapper><NotFound /></LazyRouteWrapper>} />
            <Route path="balance" element={<LazyRouteWrapper><AccountBalance /></LazyRouteWrapper>} />
            <Route path="cod-invoices" element={<LazyRouteWrapper><CodInvoices /></LazyRouteWrapper>} />
            <Route path="national-address-transactions" element={<LazyRouteWrapper><NationalAddressTransactions /></LazyRouteWrapper>} />
            <Route path="*" element={<Navigate to="/errors/404" />} />
        </Route>
    </>
)

export default merchantRoutes;
